import { useErrorStore } from "../store/errorStore";
// @ts-ignore - webpack asset module
import audioPlayerWorkletUrl from "./AudioPlayerProcessor.worklet.js";

export class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private analyserData: Uint8Array | null = null;
  private initialized = false;

  get node() {
    return this.workletNode;
  }

  async init() {
    const audioContext = new AudioContext({ sampleRate: 24000 });
    this.audioContext = audioContext;

    await audioContext.audioWorklet.addModule(audioPlayerWorkletUrl);

    this.workletNode = new AudioWorkletNode(
      audioContext,
      "audio-player-processor",
    );

    // Insert AnalyserNode between worklet and destination for real-time level metering
    this.analyserNode = audioContext.createAnalyser();
    this.analyserNode.fftSize = 256;
    this.analyserNode.smoothingTimeConstant = 0.4;
    this.analyserData = new Uint8Array(this.analyserNode.frequencyBinCount);

    this.workletNode.connect(this.analyserNode);
    this.analyserNode.connect(audioContext.destination);
    this.initialized = true;
  }

  async configure(config: { sampleRateHertz?: number; channelCount?: number; sampleSizeBits?: number; encoding?: string }) {
    if (this.audioContext && config.sampleRateHertz && config.sampleRateHertz !== this.audioContext.sampleRate) {
      this.disconnect();
      const audioContext = new AudioContext({ sampleRate: config.sampleRateHertz });
      this.audioContext = audioContext;
      await audioContext.audioWorklet.addModule(audioPlayerWorkletUrl);
      this.workletNode = new AudioWorkletNode(audioContext, "audio-player-processor");

      this.analyserNode = audioContext.createAnalyser();
      this.analyserNode.fftSize = 256;
      this.analyserNode.smoothingTimeConstant = 0.4;
      this.analyserData = new Uint8Array(this.analyserNode.frequencyBinCount);

      this.workletNode.connect(this.analyserNode);
      this.analyserNode.connect(audioContext.destination);
      this.initialized = true;
    }
  }

  /**
   * Returns the current output audio level as a 0–1 float.
   * Averages the frequency bin magnitudes from the AnalyserNode.
   */
  getAudioLevel(): number {
    if (!this.analyserNode || !this.analyserData) return 0;
    this.analyserNode.getByteFrequencyData(this.analyserData);
    let sum = 0;
    for (let i = 0; i < this.analyserData.length; i++) {
      sum += this.analyserData[i];
    }
    return sum / this.analyserData.length / 255;
  }

  bargeIn() {
    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: "barge-in",
      });
    }
  }

  stop() {
    this.bargeIn();
  }

  playAudio(samples: Float32Array) {
    if (!this.initialized || !this.workletNode) {
      console.error(
        "The audio player is not initialized. Call init() before attempting to play audio.",
      );
      return;
    }
    this.workletNode.port.postMessage({
      type: "audio",
      audioData: samples,
    });
  }

  disconnect() {
    this.stop();
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
      this.analyserData = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.initialized = false;
  }
}
